<script setup>
import { computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import SeoCopy from '@/Components/Public/SeoCopy.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import EmptyResults from '@/Components/Public/EmptyResults.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  charters:     { type: Array,  default: () => [] },
  filters:      { type: Object, default: () => ({}) },
  vessel_types: { type: Array,  default: () => [] },
  occasions:    { type: Array,  default: () => [] },
  feature_groups: { type: Object, default: () => ({}) },
  base_path:    { type: String, default: '/charters' },
  plan_path:    { type: String, default: '/plan' }
})

// Filter chips are plain links so the catalog works without JS and every
// combination is a shareable URL. The base_path already carries any
// /_p + locale prefix (see Public::ChartersController).
function chipHref(overrides) {
  const q = { ...props.filters, ...overrides }
  const parts = Object.entries(q).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
  return parts.length ? `${props.base_path}?${parts.join('&')}` : props.base_path
}
const anyFilter = computed(() => Object.values(props.filters || {}).some(v => v != null && v !== ''))
</script>

<template>
  <Head>
    <title>{{ t('public.charters.head_title') }}</title>
    <meta name="description" :content="t('public.charters.head_description')">
  </Head>

  <BrandHero kind="charter" :title="t('public.charters.title')">
    <p class="bhero-lede">{{ t('public.charters.lede') }}</p>
  </BrandHero>

  <section class="promise">
    <div class="promise__inner">
      <p class="promise__title">{{ t('public.charters.route_promise_title') }}</p>
      <p class="promise__body">{{ t('public.charters.route_promise_body') }}</p>
    </div>
  </section>

  <section class="listing">
    <div class="listing__inner">
      <nav class="chips" :aria-label="t('public.charters.filters.occasion')">
        <a :href="chipHref({ occasion: null, vessel_type: null, capacity: null })" class="chip" :class="{ 'chip--on': !anyFilter }">{{ t('public.charters.filters.all') }}</a>
        <a v-for="o in occasions" :key="o" :href="chipHref({ occasion: filters.occasion === o ? null : o })" class="chip" :class="{ 'chip--on': filters.occasion === o }">{{ t(`public.charters.occasions.${o}`) }}</a>
      </nav>
      <nav class="chips" :aria-label="t('public.charters.filters.vessel_type')">
        <a v-for="vt in vessel_types" :key="vt" :href="chipHref({ vessel_type: filters.vessel_type === vt ? null : vt })" class="chip chip--small" :class="{ 'chip--on': filters.vessel_type === vt }">{{ t(`public.charters.vessel_types.${vt}`) }}</a>
      </nav>
      <nav v-for="(keys, group) in feature_groups" :key="group" class="chips chips--features" :aria-label="t(`public.charters.features.groups.${group}`)">
        <span class="chips__label">{{ t(`public.charters.features.groups.${group}`) }}</span>
        <a v-for="f in keys" :key="f" :href="chipHref({ feature: filters.feature === f ? null : f })" class="chip chip--small" :class="{ 'chip--on': filters.feature === f }">{{ t(`public.charters.features.items.${f}`) }}</a>
      </nav>

      <EmptyResults
        v-if="charters.length === 0"
        :title="t('public.charters.empty_title')"
        :sub="t('public.charters.empty_sub')"
      />
      <ul v-else class="grid">
        <li v-for="c in charters" :key="c.id">
          <ProductCard kind="charter" :item="c" :href="`${base_path}/${c.id}`" />
        </li>
      </ul>

      <div class="plan-band">
        <p>{{ t('public.charters.plan_band') }}</p>
        <LuxButton :href="plan_path" variant="solid" tone="light" glyph="arrow">{{ t('public.charter_home.hero.cta_plan') }}</LuxButton>
      </div>
    </div>
  </section>

  <SeoCopy section="charters" />
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }

.listing { --card-accent: var(--second); background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }

.chips { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.75rem; }
.chips--secondary { margin-bottom: 1.75rem; }
.chips--features { align-items: center; }
.chips--features:last-of-type { margin-bottom: 1.75rem; }
.chips__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.25rem; }
.chip {
  display: inline-flex; align-items: center; padding: 0.55rem 1rem; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--line); color: var(--ink);
  font-size: 0.9rem; font-weight: 600; text-decoration: none; transition: border-color 150ms ease;
}
.chip--small { font-size: 0.8rem; padding: 0.4rem 0.8rem; color: var(--inkSoft); }
.chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); color: var(--ink); }
.chip--on { background: var(--night); color: var(--foam); border-color: var(--night); box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--gold) 45%, transparent); }

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr)); gap: 1.25rem; }

.plan-band {
  margin-top: 3rem; padding: 1.5rem 1.75rem; border-radius: 1.25rem;
  background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%);
  display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between;
}
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 36ch; }
/* Route promise — a private boat's day belongs to the guest. */
.promise { padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.25rem, 4vw, 3rem) 0; }
.promise__inner { max-width: 96rem; margin: 0 auto; display: grid; grid-template-columns: minmax(0, 18rem) minmax(0, 1fr); gap: 0.5rem 2.5rem; align-items: baseline; padding-bottom: clamp(1.25rem, 3vw, 2rem); border-bottom: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 40%, var(--line)); }
.promise__title { margin: 0; font-family: var(--display); font-weight: 600; font-size: 1.55rem; line-height: 1.15; color: var(--ink); }
.promise__body { margin: 0; color: var(--inkSoft); line-height: 1.55; max-width: 64ch; }
@media (max-width: 48rem) { .promise__inner { grid-template-columns: 1fr; } }
</style>
